/* ============================================================
   THE FLOWER LAB — Validador de código postal
   ============================================================
   Comprueba si un código postal cae dentro de alguna zona de
   entrega. Las zonas se definen una sola vez en el customizer
   (Configuración del tema → The Flower Lab — Envíos) y llegan
   acá como JSON dentro del propio componente.

   Marcado esperado (lo arma snippets/validador-cp-amatora.liquid):

     <div data-fl-cp>
       <script type="application/json" data-fl-cp-zonas>[...]</script>
       <form data-fl-cp-form>
         <input data-fl-cp-input>
         <button type="submit">Revisar</button>
       </form>
       <div data-fl-cp-resultado role="status"></div>
     </div>

   ------------------------------------------------------------
   Por qué los listeners van delegados en `document`
   ------------------------------------------------------------
   El validador vive dentro del panel del carrito, y ese panel se
   reemplaza entero más de una vez: al agregar un producto lo
   sustituye flowerlab-carrito.js con la Section Rendering API, y
   el theme lo rehace solo cada vez que recalcula totales.

   Enganchando el submit nodo por nodo, el validador que nacía de
   ese reemplazo quedaba mudo: el <form> no tiene `action`, así
   que al pulsar "Revisar" el navegador lo enviaba de la manera
   clásica y RECARGABA la página. Se perdía el código escrito, el
   botón de pagar volvía a bloquearse y había que empezar de cero
   una y otra vez.

   Con la delegación en `document` el submit nunca se escapa,
   venga el validador de donde venga. El MutationObserver ya solo
   se ocupa de lo suyo: repasar el código recordado en los
   validadores que aparecen después.
   ============================================================ */
(function () {
  'use strict';

  var CLAVE_MEMORIA = 'fl_cp';

  /**
   * Convierte "01000-16999, 52760" en una lista de comprobaciones.
   * Acepta rangos con guion y códigos sueltos, separados por coma.
   */
  function parsearCodigos(texto) {
    if (!texto) return [];

    return String(texto)
      .split(',')
      .map(function (parte) {
        var limpio = parte.trim();
        if (!limpio) return null;

        var guion = limpio.split('-');
        if (guion.length === 2) {
          var desde = parseInt(guion[0].trim(), 10);
          var hasta = parseInt(guion[1].trim(), 10);
          if (isNaN(desde) || isNaN(hasta)) return null;
          // Tolera que el rango venga al revés.
          return { desde: Math.min(desde, hasta), hasta: Math.max(desde, hasta) };
        }

        var exacto = parseInt(limpio, 10);
        if (isNaN(exacto)) return null;
        return { desde: exacto, hasta: exacto };
      })
      .filter(Boolean);
  }

  function buscarZona(zonas, cp) {
    var numero = parseInt(cp, 10);

    for (var i = 0; i < zonas.length; i++) {
      var rangos = zonas[i]._rangos || [];
      for (var j = 0; j < rangos.length; j++) {
        if (numero >= rangos[j].desde && numero <= rangos[j].hasta) {
          return zonas[i];
        }
      }
    }
    return null;
  }

  function aplicarPlantilla(plantilla, zona) {
    return String(plantilla || '')
      .replace(/\{zona\}/g, zona.nombre || '')
      .replace(/\{costo\}/g, zona.costo || '')
      .replace(/\{tiempo\}/g, zona.tiempo || '');
  }

  /**
   * Zonas declaradas dentro de un validador, con los rangos ya
   * calculados. Se guardan en el propio nodo: cada copia del panel
   * del carrito trae su JSON y se parsea una sola vez.
   */
  function zonasDe(raiz) {
    if (raiz._flZonas) return raiz._flZonas;

    var datos = raiz.querySelector('[data-fl-cp-zonas]');
    var zonas = [];
    try {
      zonas = JSON.parse(datos ? datos.textContent : '[]') || [];
    } catch (e) {
      zonas = [];
    }

    zonas.forEach(function (z) {
      z._rangos = parsearCodigos(z.codigos);
    });

    raiz._flZonas = zonas;
    return zonas;
  }

  function mostrar(raiz, estado, texto) {
    var salida = raiz.querySelector('[data-fl-cp-resultado]');
    if (!salida) return;

    salida.textContent = texto;
    salida.dataset.estado = estado;
    salida.hidden = false;
  }

  function limpiar(raiz) {
    var salida = raiz.querySelector('[data-fl-cp-resultado]');
    if (!salida || salida.hidden) return;

    salida.hidden = true;
    salida.textContent = '';
    delete salida.dataset.estado;
  }

  /**
   * Revisa el código escrito en ese validador y avisa del resultado.
   *
   * @param {Element} raiz  el contenedor [data-fl-cp]
   * @param {boolean} [callado]  true cuando la revisión no la pidió
   *   la persona sino la restauración del código recordado: no se
   *   marca como error un campo que todavía no llenó.
   */
  function revisar(raiz, callado) {
    var input = raiz.querySelector('[data-fl-cp-input]');
    if (!input) return;

    var msgOk = raiz.dataset.flMsgOk || 'Sí llegamos a {zona}. Envío {costo} · {tiempo}.';
    var msgNo = raiz.dataset.flMsgNo || 'Todavía no cubrimos ese código postal.';
    var msgMal = raiz.dataset.flMsgInvalido || 'Escribe un código postal de 5 dígitos.';

    var cp = (input.value || '').trim();

    if (!/^\d{5}$/.test(cp)) {
      if (!callado) mostrar(raiz, 'invalido', msgMal);
      return;
    }

    var zona = buscarZona(zonasDe(raiz), cp);

    if (zona) {
      mostrar(raiz, 'ok', aplicarPlantilla(msgOk, zona));
      try {
        localStorage.setItem(CLAVE_MEMORIA, cp);
      } catch (e) {
        /* Modo privado: no poder recordar el CP no es un error. */
      }
    } else {
      mostrar(raiz, 'no', msgNo);
    }

    anunciar({ cp: cp, zona: zona, cubierto: !!zona }, raiz);
  }

  /**
   * Publica el resultado por dos caminos a la vez.
   *
   * El evento es para quien ya esté escuchando. `window.FlowerLabCP.ultimo`
   * es para quien todavía no: flowerlab-pago-bloqueado.js se carga DESPUÉS
   * de este archivo, así que el primer resultado —el del código recordado,
   * que sale apenas arranca la página— se emitía antes de que hubiera nadie
   * del otro lado. El evento se perdía y el botón de pagar seguía cerrado
   * aunque la zona estuviera comprobada. Dejando el dato a la vista, quien
   * llegue tarde lo lee al arrancar.
   */
  function anunciar(detalle, raiz) {
    window.FlowerLabCP.ultimo = detalle;

    (raiz || document).dispatchEvent(
      new CustomEvent('flowerlab:cp:revisado', {
        bubbles: true,
        detail: detalle,
      })
    );
  }

  /**
   * Prepara un validador recién aparecido: nada de listeners —de eso se
   * encarga la delegación—, solo repone el código que la persona ya había
   * comprobado.
   *
   * Se vuelve a revisar en el momento en vez de dar por bueno el resultado
   * anterior: las zonas pueden haber cambiado desde la última visita. Y es
   * lo que evita que quien ya comprobó su zona tenga que volver a escribirla
   * cada vez que el panel del carrito se rehace.
   */
  function iniciar(raiz) {
    if (!raiz || raiz.dataset.flCpInit === '1') return;

    var input = raiz.querySelector('[data-fl-cp-input]');
    var form = raiz.querySelector('[data-fl-cp-form]');
    if (!input || !form) return;

    raiz.dataset.flCpInit = '1';

    try {
      var recordado = localStorage.getItem(CLAVE_MEMORIA);
      if (recordado && !input.value) {
        input.value = recordado;
        revisar(raiz, true);
      }
    } catch (e) {
      /* sin localStorage, simplemente arranca vacío */
    }
  }

  function iniciarTodos(ambito) {
    var raiz = ambito && ambito.querySelectorAll ? ambito : document;
    var nodos = raiz.querySelectorAll('[data-fl-cp]');
    for (var i = 0; i < nodos.length; i++) iniciar(nodos[i]);

    // `closest` hacia arriba: si el propio nodo reemplazado ES el validador,
    // querySelectorAll no lo encuentra.
    if (raiz !== document && raiz.matches && raiz.matches('[data-fl-cp]')) iniciar(raiz);
  }

  window.FlowerLabCP = {
    iniciarTodos: iniciarTodos,
    revisar: revisar,
    ultimo: null,
  };

  // ---- Listeners delegados: valen para cualquier validador, esté
  //      en la página desde el principio o lo haya traído un re-render.

  document.addEventListener(
    'submit',
    function (evento) {
      var form = evento.target;
      if (!form || !form.matches || !form.matches('[data-fl-cp-form]')) return;

      // Siempre, incluso si algo más falla: este form no tiene `action`,
      // dejarlo pasar significa recargar la página y perderlo todo.
      evento.preventDefault();

      var raiz = form.closest('[data-fl-cp]');
      if (raiz) revisar(raiz, false);
    },
    // En captura: así se adelanta a cualquier otro manejador que el theme
    // pudiera poner sobre el formulario.
    true
  );

  // Al corregir el código, se limpia el mensaje anterior.
  document.addEventListener('input', function (evento) {
    var input = evento.target;
    if (!input || !input.matches || !input.matches('[data-fl-cp-input]')) return;

    var raiz = input.closest('[data-fl-cp]');
    if (raiz) limpiar(raiz);
  });

  // ---- Arranque y validadores que llegan después

  function arrancar() {
    iniciarTodos();

    // El panel del carrito se reemplaza entero al agregar un producto y
    // cada vez que el theme recalcula totales. Sin esto, el validador que
    // sale de ese reemplazo aparece vacío y quien ya había comprobado su
    // zona tiene que escribirla de nuevo.
    var pendiente = false;
    new MutationObserver(function () {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(function () {
        pendiente = false;
        iniciarTodos();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }

  // El editor de temas reemplaza secciones sin recargar la página.
  document.addEventListener('shopify:section:load', function (evento) {
    iniciarTodos(evento.target);
  });
})();
